import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Printer, Activity } from 'lucide-react';
import { formatCATDateTime } from '../utils/timezone';
import { formatVitals, deptLabel } from './workflow/VisitBits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value, testId, bold = true, span }) => (
  <div className={span || ''}>
    <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-[13px] text-gray-900 leading-snug break-words [overflow-wrap:anywhere] ${bold ? 'font-semibold' : ''}`} data-testid={testId}>{value || '—'}</div>
  </div>
);

const Section = ({ title, aside, children, testId }) => (
  <section className="mb-4 no-break" data-testid={testId}>
    <div className="flex items-baseline justify-between border-b-2 border-gray-800 pb-0.5 mb-2">
      <h2 className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-800">{title}</h2>
      {aside && <span className="text-[11px] text-gray-500">{aside}</span>}
    </div>
    {children}
  </section>
);

const Th = ({ children, align = 'left', w }) => <th className={`text-${align} px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-600 border-b border-gray-400 ${w || ''}`}>{children}</th>;
const Td = ({ children, align = 'left', bold, muted }) => <td className={`text-${align} px-2 py-1 text-[12px] align-top border-b border-gray-200 break-words [overflow-wrap:anywhere] ${bold ? 'font-semibold text-gray-900' : muted ? 'text-gray-500' : 'text-gray-800'}`}>{children}</td>;

const Para = ({ label, value, testId, strong }) => value ? (
  <div className="grid grid-cols-[120px_1fr] gap-2 text-[12px] py-0.5">
    <div className="text-gray-500">{label}</div>
    <div className={`whitespace-pre-line text-gray-900 ${strong ? 'font-semibold' : ''}`} data-testid={testId}>{value}</div>
  </div>
) : null;

const PrintVisitSummary = () => {
  const { visitId } = useParams();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { window.location.href = '/login'; return; }
    axios.get(`${API}/visits/${visitId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setVisit(res.data))
      .catch(() => alert('Failed to load visit data'))
      .finally(() => setLoading(false));
  }, [visitId]);

  if (loading) return <div className="flex items-center justify-center min-h-screen text-lg text-gray-600">Loading visit…</div>;
  if (!visit) return <div className="flex items-center justify-center min-h-screen text-lg text-gray-600">Visit not found</div>;

  const symbol = visit.currency_symbol || '$';
  const fmt = (amount) => `${symbol}${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const live = (o) => o.status !== 'rejected' && o.status !== 'removed';

  const consultations = [...(visit.consultation_history || []), ...(visit.consultation ? [{ ...visit.consultation, department: visit.consultation.department || visit.current_department }] : [])];
  if (consultations.length === 0) consultations.push({});
  const doctorOf = (c) => c.doctor_name || '—';  // the name typed in the form, never the login username
  const doctors = [...new Set(consultations.map(doctorOf).filter((d) => d !== '—'))];

  const investigations = [
    ...(visit.lab_orders || []).filter(live).map((o) => ({ ...o, kind: o.done_by_doctor ? 'Test (by doctor)' : 'Laboratory' })),
    ...(visit.rad_orders || []).filter(live).map((o) => ({ ...o, kind: 'Radiology' })),
    ...(visit.dental_orders || []).filter(live).map((o) => ({ ...o, kind: 'Dental' })),
  ];
  const notPerformed = [...(visit.lab_orders || []), ...(visit.rad_orders || [])].filter((o) => o.status === 'rejected').length;
  const testsDone = investigations.filter((o) => o.status === 'completed');
  const testsTotal = testsDone.reduce((s, o) => s + (o.cost || 0) * (o.quantity || 1), 0);
  const medications = (visit.prescriptions || []).filter((p) => p.status === 'dispensed' || p.status === 'pending');
  const medsTotal = medications.reduce((s, p) => s + (p.cost || 0) * (p.quantity || 1), 0);
  const services = visit.service_items || [];
  const servicesTotal = services.reduce((s, i) => s + (i.cost || 0) * (i.quantity || 1), 0);
  const grandTotal = testsTotal + medsTotal + servicesTotal;

  return (
    <>
      <style>{`
        @media print {
          .no-break { page-break-inside: avoid; break-inside: avoid; }
          @page { size: A4; margin: 12mm 14mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="min-h-screen bg-gray-100 p-6 print:p-0 print:bg-white">
        <div className="max-w-[210mm] mx-auto mb-4 print:hidden">
          <div className="bg-white shadow rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-gray-600"><strong>Visit Summary</strong> — take-home clinical record (A4)</div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => window.print()} className="bg-teal-600 text-white hover:bg-teal-700 font-semibold" data-testid="print-visit-summary-button">
                <Printer className="w-4 h-4 mr-2" /> Print Summary
              </Button>
              {visit.external_referral?.status === 'approved' && (
                <Button onClick={() => window.open(`/print-referral/${visit.visit_id}`, '_blank')} variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50 font-semibold" data-testid="open-referral-letter">Referral Letter</Button>
              )}
              {visit.admission?.discharged_at && (
                <Button onClick={() => window.open(`/print-discharge/${visit.visit_id}`, '_blank')} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold" data-testid="open-discharge-letter">Discharge Letter</Button>
              )}
              <Button onClick={() => window.close()} variant="outline" className="bg-white text-gray-700 hover:bg-gray-100 font-semibold border-2 border-gray-300">Close</Button>
            </div>
          </div>
        </div>

        <div className="max-w-[210mm] mx-auto bg-white shadow-lg print:shadow-none">
          <div className="p-[12mm] print:p-0 text-gray-900" data-testid="visit-summary-content">
            {/* Header */}
            <header className="flex items-start justify-between gap-4 border-b-4 border-gray-900 pb-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-md bg-gray-900 flex items-center justify-center print:bg-gray-900">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-extrabold tracking-tight leading-tight">Patient Visit Summary</h1>
                  <p className="text-[13px] font-medium text-gray-700">{visit.hospital_name}</p>
                  <p className="text-[11px] text-gray-500">Acacia Medical Insurance — clinical record for the patient</p>
                </div>
              </div>
              <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-[11px] text-right">
                <span className="text-gray-500">Visit ID</span><span className="font-mono font-bold text-gray-900" data-testid="visit-summary-id">{visit.visit_id}</span>
                <span className="text-gray-500">Checked in</span><span className="font-medium">{formatCATDateTime(visit.checked_in_at)}</span>
                {visit.preauth?.number && <><span className="text-gray-500">Pre-auth #</span><span className="font-mono font-bold" data-testid="visit-summary-preauth">{visit.preauth.number}</span></>}
                {visit.billing?.claim_id && <><span className="text-gray-500">Claim ID</span><span className="font-mono font-bold" data-testid="visit-summary-claim">{visit.billing.claim_id}</span></>}
                {visit.admission?.number && <><span className="text-gray-500">Admission #</span><span className="font-mono font-bold">{visit.admission.number}</span></>}
              </div>
            </header>

            {/* Patient + visit facts */}
            <div className="grid grid-cols-[1fr_auto] gap-4 mb-4 no-break">
              <div className="border border-gray-300 rounded-md p-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-2">Patient</div>
                <div className="grid grid-cols-4 gap-x-4 gap-y-2">
                  <Field label="Full name" value={visit.patient_name} testId="visit-summary-patient" span="col-span-2" />
                  <Field label="Serial number" value={visit.patient_serial_number} />
                  <Field label="Family ID" value={visit.family_id} />
                  <Field label="Date of birth" value={visit.patient_dob || 'N/A'} />
                  <Field label="Sex" value={visit.patient_sex || 'N/A'} />
                  <Field label="Relationship" value={(visit.patient_relationship === 'Principle' ? 'Principal' : visit.patient_relationship) || 'N/A'} />
                  {(visit.client_phone || visit.client_location) && <Field label="Contact" value={[visit.client_phone, visit.client_location].filter(Boolean).join(' • ')} bold={false} />}
                </div>
              </div>
              <div className="border border-gray-300 rounded-md p-3 min-w-[62mm]">
                <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-2">Visit</div>
                <div className="space-y-2">
                  <Field label="Reason for visit" value={visit.reason || 'N/A'} bold={false} />
                  <Field label={doctors.length > 1 ? 'Doctors seen' : 'Attending doctor'} value={doctors.join(', ')} testId="visit-summary-doctor" />
                  {visit.specialist && <Field label="Specialist referral" value={visit.specialist} testId="visit-summary-specialist" />}
                  {visit.external_referral?.status === 'approved' && <Field label="Referred out to" value={`${visit.external_referral.specialist} — ${visit.external_referral.facility}`} bold={false} />}
                </div>
              </div>
            </div>

            {/* Consultations */}
            {consultations.map((c, i) => (
              <Section key={i} title={consultations.length > 1 ? `Consultation ${i + 1} — ${deptLabel(c.department, visit)}` : 'Consultation'} aside={`${doctorOf(c)}${c.department ? ` · ${deptLabel(c.department, visit)}` : ''}${c.completed_at ? ` · ${formatCATDateTime(c.completed_at)}` : ''}`}>
                <Para label="Vital signs" value={formatVitals(c.vitals)} testId={i === 0 ? 'visit-summary-vitals' : undefined} />
                <Para label="Complaints" value={c.complaints} />
                <Para label="Examination" value={c.notes} />
                <Para label="Provisional Dx" value={c.provisional_diagnosis || c.diagnosis || 'N/A'} testId={i === 0 ? 'visit-summary-diagnosis' : undefined} strong />
                <Para label="Final diagnosis" value={c.final_diagnosis} testId={i === 0 ? 'visit-summary-final-diagnosis' : undefined} strong />
              </Section>
            ))}

            {/* Admission */}
            {visit.admission?.number && (
              <Section title={`Admission — ${visit.admission.number}`} aside={visit.admission.ward ? `Ward: ${visit.admission.ward}` : undefined} testId="visit-summary-admission">
                <div className="grid grid-cols-3 gap-3 mb-1">
                  <Field label="Admitted" value={formatCATDateTime(visit.admission.approved_at)} bold={false} />
                  <Field label="Discharged" value={visit.admission.discharged_at ? `${formatCATDateTime(visit.admission.discharged_at)}${visit.admission.bed_days ? ` (${visit.admission.bed_days} bed day${visit.admission.bed_days > 1 ? 's' : ''})` : ''}` : 'Still admitted'} bold={false} />
                  <Field label="Reason" value={visit.admission.reason} bold={false} />
                </div>
                {(visit.rounds || []).length > 0 && (
                  <div className="mt-1 border-l-2 border-gray-300 pl-2 space-y-0.5">
                    {(visit.rounds || []).map((r) => (
                      <div key={r.round_id} className="text-[11px]">
                        <span className="text-gray-500">{formatCATDateTime(r.at)}{(r.by_name) ? ` — ${r.by_name}` : ''}</span>{formatVitals(r.vitals) && <span className="text-gray-600"> • {formatVitals(r.vitals)}</span>}
                        {r.notes && <div className="text-gray-800 whitespace-pre-line">{r.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
                <Para label="Discharge summary" value={visit.admission.discharge_summary} testId="visit-summary-discharge" />
              </Section>
            )}

            {/* Investigations */}
            {investigations.length > 0 && (
              <Section title="Investigations & Results" aside={notPerformed > 0 ? `${notPerformed} ordered item(s) not performed (not approved)` : undefined}>
                <table className="w-full table-fixed border-collapse">
                  <thead><tr><Th w="w-[16%]">Type</Th><Th w="w-[28%]">Test / Scan</Th><Th>Result / Findings</Th><Th align="right" w="w-[14%]">Amount</Th></tr></thead>
                  <tbody data-testid="visit-summary-labs">
                    {investigations.map((o, i) => (
                      <tr key={o.order_id || `${o.item_id}-${i}`} data-testid={o.kind === 'Radiology' ? 'visit-summary-rad-row' : 'visit-summary-lab-row'}>
                        <Td muted>{o.kind}</Td>
                        <Td bold>{o.item_name}{(o.quantity || 1) > 1 ? ` ×${o.quantity}` : ''}</Td>
                        <Td>{o.result || (o.status === 'pending' ? 'Pending' : '—')}</Td>
                        <Td align="right">{fmt(o.cost * (o.quantity || 1))}</Td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr><td colSpan={3} className="px-2 py-1 text-[11px] text-right text-gray-600">Total for tests performed</td><td className="px-2 py-1 text-right text-[12px] font-bold" data-testid="tests-total">{fmt(testsTotal)}</td></tr></tfoot>
                </table>
              </Section>
            )}

            {/* Medications */}
            {medications.length > 0 && (
              <Section title="Medications & Dosage Instructions">
                <table className="w-full table-fixed border-collapse">
                  <thead><tr><Th w="w-[34%]">Medication</Th><Th align="center" w="w-[8%]">Qty</Th><Th>Instructions</Th><Th align="right" w="w-[14%]">Amount</Th></tr></thead>
                  <tbody data-testid="visit-summary-medications">
                    {medications.map((p, i) => (
                      <tr key={p.rx_id || `${p.item_id}-${i}`}>
                        <Td bold>{p.item_name}</Td>
                        <Td align="center">{p.quantity || 1}</Td>
                        <Td>{p.instructions || 'As directed by the doctor'}</Td>
                        <Td align="right">{fmt(p.cost * (p.quantity || 1))}</Td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr><td colSpan={3} className="px-2 py-1 text-[11px] text-right text-gray-600">Medications total</td><td className="px-2 py-1 text-right text-[12px] font-bold">{fmt(medsTotal)}</td></tr></tfoot>
                </table>
              </Section>
            )}

            {/* Services + totals side by side */}
            <div className={`grid ${services.length > 0 ? 'grid-cols-[1fr_64mm]' : 'grid-cols-1'} gap-4 no-break`}>
              {services.length > 0 && (
                <Section title="Consultation Fees & Services">
                  <table className="w-full table-fixed border-collapse">
                    <thead><tr><Th>Service</Th><Th align="center" w="w-[14%]">Qty</Th><Th align="right" w="w-[26%]">Amount</Th></tr></thead>
                    <tbody>
                      {services.map((s, i) => (
                        <tr key={s.service_id || `${s.item_id}-${i}`}><Td bold>{s.item_name}</Td><Td align="center">{s.quantity || 1}</Td><Td align="right">{fmt(s.cost * (s.quantity || 1))}</Td></tr>
                      ))}
                    </tbody>
                    <tfoot><tr><td colSpan={2} className="px-2 py-1 text-[11px] text-right text-gray-600">Services total</td><td className="px-2 py-1 text-right text-[12px] font-bold">{fmt(servicesTotal)}</td></tr></tfoot>
                  </table>
                </Section>
              )}
              <Section title="Summary of Charges">
                <div className="text-[12px] space-y-1">
                  <div className="flex justify-between"><span className="text-gray-600">Tests performed</span><span>{fmt(testsTotal)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Medications</span><span>{fmt(medsTotal)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Fees & services</span><span>{fmt(servicesTotal)}</span></div>
                  <div className="flex justify-between border-t-2 border-gray-800 pt-1 mt-1 text-[14px] font-extrabold">
                    <span>{visit.billing ? 'Total billed' : 'Running total'}</span>
                    <span data-testid="visit-summary-total">{visit.billing ? `${fmt(visit.billing.total_amount_local)} (${visit.billing.currency_code})` : fmt(grandTotal)}</span>
                  </div>
                  {visit.billing?.billed_at && <div className="text-[10px] text-gray-500 text-right">Billed {formatCATDateTime(visit.billing.billed_at)} — covered by Acacia Medical Insurance</div>}
                </div>
              </Section>
            </div>

            {/* Sign-off */}
            <footer className="mt-6 pt-3 border-t border-gray-300 no-break">
              <div className="grid grid-cols-2 gap-8 text-[11px]">
                <div>
                  <div className="h-8 border-b border-gray-500" />
                  <div className="mt-1 text-gray-600">Attending doctor: <span className="font-semibold text-gray-900">{doctors[doctors.length - 1] || '—'}</span></div>
                </div>
                <div>
                  <div className="h-8 border-b border-gray-500" />
                  <div className="mt-1 text-gray-600">Hospital stamp &amp; date</div>
                </div>
              </div>
              <p className="mt-3 text-center text-[10px] text-gray-500">Computer-generated visit summary from the Acacia Insurance system — keep it for your medical records. Printed {formatCATDateTime(new Date().toISOString())}. Get well soon — {visit.hospital_name}.</p>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintVisitSummary;
